import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from './auth-code-flow.config';
import { AuthModeService } from './auth-mode.service';
import { getPathFromBrowser, isErrorPath } from './browser-path.util';
import { encodePowerBiDeepLinkState } from './powerbi-deeplink-state';

const POWERBI_REPORT_ID_KEY = 'powerbi_reportId';
const POWERBI_REPORT_PARAMS_KEY = 'powerbi_reportParams';

/**
 * Prompts the user to log in: IdP mode configures OAuth, loads discovery, then starts the login flow;
 * CoT mode navigates to home. No-op when already on the error page.
 * When a Power BI report deep-link is in sessionStorage, encodes it into OAuth state so the report
 * opens after login even if the callback lands in a different tab/window.
 */
@Injectable({ providedIn: 'root' })
export class AuthPromptService {
  constructor(
    private readonly router: Router,
    private readonly oauthService: OAuthService,
    private readonly authModeService: AuthModeService
  ) {}

  promptLogin(force = false): void {
    const pathFromRouter = this.router.url.split('?')[0];
    const pathFromBrowser = getPathFromBrowser();
    if (!force && (pathFromRouter === '/error' || isErrorPath(pathFromBrowser))) {
      return;
    }
    if (this.authModeService.isIdp()) {
      this.oauthService.configure(authCodeFlowConfig);
      this.oauthService.loadDiscoveryDocument()
        .then(() => {
          const state = this.getPowerBiDeepLinkState();
          console.log('[PowerBI DeepLink] promptLogin: getPowerBiDeepLinkState() =>', state ? 'state length ' + state.length + ' (starts with pbi: ' + state.startsWith('pbi:') + ')' : null);
          this.oauthService.initLoginFlow(state || undefined);
        })
        .catch((err) => console.error('[AuthPrompt] loadDiscoveryDocument failed', err));
    } else {
      this.router.navigate(['/']);
    }
  }

  /** Build OAuth state from persisted Power BI deep-link so callback can restore it. */
  private getPowerBiDeepLinkState(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    const reportId = sessionStorage.getItem(POWERBI_REPORT_ID_KEY)?.trim();
    console.log('[PowerBI DeepLink] getPowerBiDeepLinkState: powerbi_reportId from sessionStorage=', reportId ?? '(empty)');
    if (!reportId) return null;
    let reportParams: Record<string, string | string[]> | undefined;
    const raw = sessionStorage.getItem(POWERBI_REPORT_PARAMS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') reportParams = parsed;
      } catch {
        // ignore
      }
    }
    return encodePowerBiDeepLinkState({ reportId, reportParams }) || null;
  }
}
